// Dark Matter speed-ups (G-85, G-86, P37): finish the running build or ship batch now. The price
// follows the countdown; the server charges its own price, never more than shown.
import { type SpeedUpTargetKind, speedUpCost } from '@stars-and-ships/api-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { endpoints } from '../../api/endpoints';
import { ApiRequestError } from '../../api/http';
import { invalidateSoon } from '../../api/invalidate';
import { queryKeys } from '../../api/queries';
import { formatDuration } from '../../labels';
import { Button, DarkMatterAmount, DarkMatterIcon, ErrorNotice, Sheet } from '../ui';
import type { FinishNowContext } from './types';

export function FinishNowButton({
  context,
  kind,
  itemId,
  endsAt,
  now,
  title,
}: {
  context: FinishNowContext;
  kind: SpeedUpTargetKind;
  itemId: string;
  endsAt: number;
  now: number;
  /** Sheet title, e.g. "Finish Metal Mine Lv 8 now?". */
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const left = endsAt - now;
  const cost = speedUpCost(left, context.darkMatterPerHour);
  if (cost === 0) return null;
  const short = cost > context.darkMatter;
  return (
    <>
      <Button variant="ghost" disabled={short} onClick={() => setOpen(true)}>
        {short ? (
          <>
            Not enough <DarkMatterIcon />
            <span className="sr-only">Dark Matter</span>
          </>
        ) : (
          <>
            ⚡ Finish now · <DarkMatterAmount value={cost} />
          </>
        )}
      </Button>
      {open ? (
        <FinishNowSheet
          context={context}
          kind={kind}
          itemId={itemId}
          left={left}
          cost={cost}
          title={title}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function FinishNowSheet({
  context,
  kind,
  itemId,
  left,
  cost: shownCost,
  title,
  onClose,
}: {
  context: FinishNowContext;
  kind: SpeedUpTargetKind;
  itemId: string;
  left: number;
  cost: number;
  title: string;
  onClose(): void;
}) {
  const queryClient = useQueryClient();
  // COST_CHANGED carries the server's price (a client clock running ahead shows too little); confirm with it.
  const [serverCost, setServerCost] = useState<number | null>(null);
  const cost = serverCost ?? shownCost;
  const { galaxyId } = context;
  const finish = useMutation({
    mutationFn: () =>
      endpoints.speedUp(galaxyId, {
        targetKind: kind,
        targetId: itemId,
        expectedCostDarkMatter: cost,
      }),
    onSettled: () =>
      invalidateSoon(queryClient, [
        queryKeys.systems(galaxyId),
        queryKeys.galaxyMe(galaxyId),
        queryKeys.fleets(galaxyId),
      ]),
    onSuccess: onClose,
    onError: (error) => {
      const fresh = error instanceof ApiRequestError ? error.details?.cost : undefined;
      if (
        error instanceof ApiRequestError &&
        error.code === 'COST_CHANGED' &&
        typeof fresh === 'number'
      ) {
        setServerCost(fresh);
      }
    },
  });
  return (
    <Sheet open title={title} onClose={onClose}>
      <dl className="space-y-1 text-sm">
        <div>
          <dt className="inline text-muted">Saves </dt>
          <dd className="inline font-mono tabular-nums">{formatDuration(left)}</dd>
        </div>
        <div>
          <dt className="inline text-muted">Costs </dt>
          <dd className="inline font-mono tabular-nums">
            <DarkMatterAmount value={cost} />
          </dd>
        </div>
        <div>
          <dt className="inline text-muted">Balance after </dt>
          <dd className="inline font-mono tabular-nums">
            <DarkMatterAmount value={context.darkMatter - cost} />
          </dd>
        </div>
      </dl>
      {finish.error ? <ErrorNotice error={finish.error} /> : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          pending={finish.isPending}
          disabled={cost > context.darkMatter}
          onClick={() => finish.mutate()}
        >
          Finish now
        </Button>
      </div>
    </Sheet>
  );
}
